"use client";

import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, animate } from "framer-motion";
import BuyPlan from "./PlanPurchase/BuyPlan";
import Tooltip from "../components/Tooltip";

// ─── Switch ──────────────────────────────────────────────────────────────────
const Switch = React.forwardRef(
  ({ checked: controlledChecked, onCheckedChange, defaultChecked = false, disabled = false, size = "md", ...props }, ref) => {
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const isControlled = controlledChecked !== undefined;
    const checkedState = isControlled ? controlledChecked : internalChecked;

    const handleToggle = () => {
      if (disabled) return;
      const next = !checkedState;
      if (!isControlled) setInternalChecked(next);
      onCheckedChange?.(next);
    };
    const handleKeyDown = (e) => {
      if (e.key === " " || e.key === "Enter") { e.preventDefault(); handleToggle(); }
    };

    const sizes = {
      sm: { track: { width: 40, height: 24 }, thumb: 20, checkedX: 18, uncheckedX: 2 },
      md: { track: { width: 56, height: 32 }, thumb: 28, checkedX: 24, uncheckedX: 2 },
      lg: { track: { width: 72, height: 40 }, thumb: 36, checkedX: 32, uncheckedX: 2 },
    };
    const s = sizes[size] || sizes.md;

    const thumbVariants = {
      tap: { width: s.thumb + 4, x: checkedState ? s.checkedX - 2 : s.uncheckedX, transition: { duration: 0.15 } },
      checked: { x: s.checkedX, width: s.thumb, transition: { type: "spring", stiffness: 500, damping: 35 } },
      unchecked: { x: s.uncheckedX, width: s.thumb, transition: { type: "spring", stiffness: 500, damping: 35 } },
    };
    const trackVariants = {
      checked: { backgroundColor: "#572e94" },
      unchecked: { backgroundColor: "#d1d5db" },
    };

    return (
      <motion.button
        ref={ref} role="switch" type="button" aria-checked={checkedState}
        disabled={disabled} onClick={handleToggle} onKeyDown={handleKeyDown}
        animate={checkedState ? "checked" : "unchecked"}
        whileTap={!disabled ? "tap" : undefined}
        variants={trackVariants} transition={{ duration: 0.2 }}
        style={{ display: "inline-flex", alignItems: "center", width: s.track.width, height: s.track.height, flexShrink: 0, borderRadius: 9999, position: "relative", border: "none", padding: 0, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, outline: "none", WebkitTapHighlightColor: "transparent" }}
        {...props}
      >
        <motion.span
          initial={{ x: defaultChecked ? s.checkedX : s.uncheckedX }}
          variants={thumbVariants}
          style={{ display: "block", height: s.thumb, backgroundColor: "#ffffff", borderRadius: 9999, boxShadow: "0 2px 6px rgba(0,0,0,0.25)", position: "absolute", top: (s.track.height - s.thumb) / 2 }}
        />
      </motion.button>
    );
  }
);
Switch.displayName = "Switch";

// ─── Plan Data ───────────────────────────────────────────────────────────────
const ALL_PLAN_DETAILS = {
  SaayaaSuraksha: {
    "1yr": { price: "₹2,499", validity: "1 Year", planCode: "SaayaaSuraksha" },
    "2yr": { price: "₹4,500", validity: "2 Years", planCode: "SaayaaSuraksha2yr" },
  },
  SaayaaSurakshaPlus: {
    "1yr": { price: "₹3,499", validity: "1 Year", planCode: "SaayaaSurakshaPlus" },
    "2yr": { price: "₹6,300", validity: "2 Years", planCode: "SaayaaSurakshaPlus2Y" },
  },
  SaayaaSurakshaShield: {
    "1yr": { price: "₹4,999", validity: "1 Year", planCode: "SaayaaSurakshaShield" },
    "2yr": { price: "₹9,000", validity: "2 Years", planCode: "SaayaaSurakshaShield2Y" },
  },
  SaayaaParivaarSuraksha: {
    "1yr": { price: "₹8,499", validity: "1 Year", planCode: "SaayaaParivaarSuraksha" },
    "2yr": { price: "₹15,300", validity: "2 Years", planCode: "SaayaaParivaarSuraksha2Y" },
  },
  SaayaaSeniorKavach: {
    "1yr": { price: "₹9,999", validity: "1 Year", planCode: "SaayaaSeniorKavach1yr" },
    "2yr": { price: "₹17,000", validity: "2 Years", planCode: "SaayaaSeniorKavach" },
  },
  SaayaaSurakshaV2: {
    "1yr": { price: "₹9,999", validity: "1 Year", planCode: "SaayaaSurakshaV2" },
  },
};

const PLANS_META = [
  {
    key: "SaayaaSuraksha", name: "Saayaa Suraksha", tagline: "Best for individuals",
    badge: "Most Popular", members: 0,
    highlights: ["8 Tele Consultations (GP, Ayurveda, Homeopathy)", "2 Specialist Consultations (18 types)", "4 Mental Wellness Sessions", "4 Video Consultations – GP", "Annual Health Checkup (Home Collection)", "1 Dental Checkup", "1 Pet Care Consultation"],
    sections: [
      { heading: null, items: ["8 Consultations on Tele Consultation (General Physician)", "8 Consultations on Tele Consultation (Ayurveda)", "8 Consultations on Tele Consultation (Homeopathy)", { text: "2 Consultations on Tele Consultation", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" }, "4 Consultations on Tele Mental Wellness", "2 Consultations on Dietician Consultation", "4 Consultations on Video Consultation – General Physician", { text: "1 Consultation on", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Cholesterol Total, Kidney Function Test with Electrolytes, Thyroid Function Test (TFT), Urine Complete Analysis", label: "Annual Health CheckUp – Basic", suffix: "(Home Collection)" }, "1 Consultation on Dental Checkup & Consultation", "1 Video Consultation on Pet Care – Dogs & Cats"] },
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹20,000 on Diagnostic & Radiology", "Upto 20% off on upto ₹10,000 on Prescribed Scheduled Medicines"] },
    ],
  },
  {
    key: "SaayaaSurakshaPlus", name: "Saayaa Suraksha Plus", tagline: "Best for individuals",
     batch:null,members: 0,
    highlights: ["8 Tele Consultations (GP, Ayurveda, Homeopathy)", "4 Specialist Consultations (18 types)", "6 Video Consultations – GP", "2 In-person GP Consultations", "Annual Health Checkup (Home Collection)", "₹3L Accident Insurance"],
    sections: [
      { heading: null, items: ["8 Consultations on Tele Consultation (General Physician)", "8 Consultations on Tele Consultation (Ayurveda)", "8 Consultations on Tele Consultation (Homeopathy)", { text: "4 Consultations on Tele Consultation", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" }, "4 Consultations on Tele Mental Wellness", "4 Consultations on Dietician Consultation", "6 Consultations on Video Consultation – General Physician", "2 In-person Consultations with Our Specialists at a Clinic or Hospital / My Choice Doctor", { text: "1 Consultation on", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Cholesterol Total, Kidney Function Test with Electrolytes, Thyroid Function Test (TFT), Urine Complete Analysis", label: "Annual Health CheckUp – Basic", suffix: "(Home Collection)" }, "2 Consultations on Dental Checkup & Consultation", "2 Video Consultations on Pet Care – Dogs & Cats"] },
      { heading: "Group Personal Accident", headingSub: "offered in association with Care Health Insurance", items: ["₹3 Lakhs Sum Insured", "Covered Accidental Death (100% SI Paid on death)", "Covered Permanent Partial Disability (Upto 100% SI Paid on PTD)", "Covered Permanent Partial Disability (Upto 100% SI Paid on PPD)", "₹500/day Hospitalization Daily Cash", "Upto ₹5,000 Accidental Hospitalisation OPD", "Upto ₹25,000 Accidental Hospitalisation IPD"] },
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹20,000 on Diagnostic & Radiology", "Upto 20% off on upto ₹10,000 on Prescribed Scheduled Medicines"] },
    ],
  },
  {
    key: "SaayaaSurakshaShield", name: "Saayaa Suraksha Shield", tagline: "Best for individuals",
    badge: "Premium", members: 0,
    highlights: ["12 Tele Consultations (GP, Ayurveda, Homeopathy)", "6 Specialist Consultations (18 types)", "8 Video + 6 In-person GP Consultations", "Annual Health Checkup (Home Collection)", "₹4L Accident Insurance"],
    sections: [
      { heading: null, items: ["12 Consultations on Tele Consultation (General Physician)", "12 Consultations on Tele Consultation (Ayurveda)", "12 Consultations on Tele Consultation (Homeopathy)", { text: "6 Consultations on Tele Consultation", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" }, "4 Consultations on Tele Mental Wellness", "4 Consultations on Dietician Consultation", "8 Consultations on Video Consultation – General Physician", "6 In-person Consultations with Our Specialists at a Clinic or Hospital / My Choice Doctor", { text: "1 Consultation on", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Cholesterol Total, Kidney Function Test with Electrolytes, Thyroid Function Test (TFT), Urine Complete Analysis", label: "Annual Health CheckUp – Basic", suffix: "(Home Collection)" }, "2 Consultations on Dental Checkup & Consultation", "2 Video Consultations on Pet Care – Dogs & Cats"] },
      { heading: "Group Personal Accident", headingSub: "offered in association with Care Health Insurance", items: ["₹4 Lakhs Sum Insured", "Covered Accidental Death (100% SI Paid on death)", "Covered Permanent Total Disability (Upto 100% SI Paid on PTD)", "Covered Permanent Partial Disability (Upto 100% SI Paid on PPD)", "₹500/day Hospitalization Daily Cash", "Upto ₹5,000 Accidental Hospitalisation OPD", "Upto ₹25,000 Accidental Hospitalisation IPD"]},
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹30,000 on Diagnostic & Radiology", "Upto 20% off on upto ₹15,000 on Prescribed Scheduled Medicines"] },
    ],
  },
  {
    key: "SaayaaParivaarSuraksha", name: "Saayaa Parivaar Suraksha", tagline: `Best for families up to 60 Years (2 Adults + 2 Children (age 0-30))`,
    badge: "Family", members: 3,
    highlights: ["Unlimited Tele Consultations (GP, Ayurveda, Homeopathy, 18 Specialists)", "8 Video + 8 In-person GP Consultations", "2 Annual Health Checkups", "₹5L Accident Insurance", "4 Dental Checkups"],
    sections: [
      { heading: null, items: ["Unlimited Tele Consultation (General Physician)", "Unlimited Tele Consultation (Ayurveda)", "Unlimited Tele Consultation (Homeopathy)", { text: "Unlimited Tele Consultation", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" }, "4 Consultations on Tele Mental Wellness", "4 Consultations on Dietician Consultation", "8 Consultations on Video Consultation – General Physician", "8 In-person Consultations with Our Specialists at a Clinic or Hospital / My Choice Doctor", { text: "2 Consultations on", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Cholesterol Total, Kidney Function Test with Electrolytes, Thyroid Function Test (TFT), Urine Complete Analysis", label: "Annual Health CheckUp – Basic", suffix: "(Home Collection)" }, "4 Consultations on Dental Checkup & Consultation", "2 Video Consultations on Pet Care – Dogs & Cats"] },
      { heading: "Group Personal Accident", headingSub: "offered in association with Care Health Insurance", items: ["₹5 Lakhs Sum Insured", "Covered Accidental Death (100% SI)", "Covered Permanent Total Disability  (Upto 100% SI Paid on PTD)", "Covered Permanent Partial Disability  (Upto 100% SI Paid on PPD)", "₹500/day Hospitalization Daily Cash", "Upto ₹5,000 Accidental Hospitalisation OPD", "Upto ₹25,000 Accidental Hospitalisation IPD"] },
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹50,000 on Diagnostic & Radiology", "Upto 20% off on upto ₹30,000 on Prescribed Scheduled Medicines"] },
    ],
  },
  {
    key: "SaayaaSeniorKavach", name: "Saayaa Senior Kavach", tagline: "Best for individuals above 60+ Years",
    badge: "Senior Care", members: 0,
    highlights: ["Unlimited Tele Consultations (GP, Ayurveda, Homeopathy, 18 Specialists)", "8 Mental Wellness + 8 Dietician Sessions", "8 Video + 6 In-person GP Consultations", "2 Enhanced Health Checkups", "2 Dental Checkups"],
    sections: [
      { heading: null, items: ["Unlimited Tele Consultation (General Physician)", "Unlimited Tele Consultation (Ayurveda)", "Unlimited Tele Consultation (Homeopathy)", { text: "Unlimited Tele Consultation", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" }, "8 Consultations on Tele Mental Wellness", "8 Consultations on Dietician Consultation", "8 Consultations on Video Consultation – General Physician", "6 In-person Consultations with Our Specialists at a Clinic or Hospital / My Choice Doctor", { text: "2 Consultations on", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Vitamin B12, Glycosylated Haemoglobin (HbA1c), Fasting Blood Sugar (FBS), Thyroid Function Test (TFT), Vitamin D, 25-Hydroxy, Kidney Function Test with Electrolytes, Lipid Profile, Urine Complete Analysis", label: "Annual Health CheckUp – Essentials", suffix: "(Home Collection)" }, "2 Consultations on Dental Checkup & Consultation"] },
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹50,000 on Diagnostic & Radiology", "Upto 20% off on upto ₹30,000 on Prescribed Scheduled Medicines"] },
    ],
  },
  {
    key: "SaayaaSurakshaV2", name: "Saayaa Suraksha V2", tagline: "Best for individuals · Wallet-based plan", members: 0,
    highlights: [
      "₹5,000 Wallet – Tele Consultations (GP, Ayurveda, Homeopathy & 18 Specialists)",
      "₹2,000 Wallet – Tele Mental Wellness",
      "₹5,000 Wallet – Video Consultation (GP)",
      "₹10,000 Wallet – In-person GP Consultation",
      "1 Credit – Annual Health Checkup (Essentials)",
      "₹5,000 Diagnostic & Radiology Wallet",
      "₹3,000 Pharmacy Wallet – Prescribed Medicines",
    ],
    sections: [
      { heading: null, items: [
        { text: "₹5,000 Wallet for Tele Consultation (General Physician), (Ayurveda), (Homeopathy) &", tooltip: "General Physician, Ayurvedic, Homeopathy, Gynecologist, Pediatric, Gastroenterologist, ENT, Cardiologist, Sexologist, Opthal, Dental, Orthopedic, Dietician, Endocrinologists, Urologist, Neurologist, Cough and Fever, Dermatologist", label: "(18 Specialist)" },
        "₹2,000 Wallet for Tele Mental Wellness",
        "₹5,000 Wallet for Dietician Consultation",
        "₹5,000 Wallet for Video Consultation – General Physician",
        "₹10,000 Wallet for In-person General Physician Clinic & Hospital Consultation / My Choice Doctor",
        { text: "1 Credit for", tooltip: "Complete Blood Count (CBC), Liver Function Test (LFT), Blood Sugar, Vitamin B12, Glycosylated Haemoglobin (HbA1c), Fasting Blood Sugar (FBS), Thyroid Function Test (TFT), Vitamin D, 25-Hydroxy, Kidney Function Test with Electrolytes, Lipid Profile, Urine Complete Analysis", label: "Annual Health CheckUp – Essentials", suffix: "(Home Collection). Basic Health Checkup is not applicable on this plan." },
        "₹5,000 Diagnostic & Radiology Wallet",
        "₹3,000 Pharmacy Wallet – Prescribed Medicines",
        "₹3,000 Wallet for Dental Checkup & Consultation",
        "₹5,000 Wallet for Pet Care Video Consultation – Dogs & Cats",
      ] },
      { heading: "Value Added Services", items: ["Unlimited – Risk Assessment", "Unlimited – Health Reminder", "Unlimited – Recorded Fitness Sessions"] },
      { heading: "Platform Benefits", items: ["Upto 50% off on upto ₹20,000 on Diagnostic Test & Radiology", "Upto 20% off on upto ₹10,000 on Prescribed Scheduled Medicines"] },
    ],
  }
];

const GAP = 16;

// ─── Responsive card sizes based on viewport ─────────────────────────────────
// FIX: Previously CARD_W=320 and ACTIVE_W=340 were module-level constants,
// meaning on a 375px mobile screen a single card was already 340px — nearly
// full width — leaving no room for peeking adjacent cards or correct centering.
// Now we derive sizes from the live viewport width.
function getCardSizes(viewportW) {
  if (viewportW < 480) {
    // Mobile: active card fills ~85% of screen, inactive peek at ~60%
    const activeW = Math.min(Math.floor(viewportW * 0.82), 320);
    const cardW = Math.floor(activeW * 0.72);
    return { CARD_W: cardW, ACTIVE_W: activeW };
  }
  if (viewportW < 768) {
    return { CARD_W: 260, ACTIVE_W: 300 };
  }
  // Desktop
  return { CARD_W: 320, ACTIVE_W: 340 };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const CheckIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 3 }}>
    <circle cx="8" cy="8" r="8" fill={active ? "#572e94" : "#1AAD9D"} fillOpacity="0.13" />
    <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke={active ? "#572e94" : "#1AAD9D"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SectionLabel = ({ heading, sub }) => (
  <div className="mt-5 mb-2.5 pb-2 border-b border-gray-100">
    <p className="text-xs font-bold text-gray-800 uppercase tracking-wider">{heading}</p>
    {sub && <p className="text-xs text-[#1AAD9D] mt-0.5">{sub}</p>}
  </div>
);

const BADGE_COLORS = {
  "Most Popular": { bg: "#572e94", text: "#fff" },
  "Premium":      { bg: "#0E8F82", text: "#fff" },
  "Family":       { bg: "#2563eb", text: "#fff" },
  "Senior Care":  { bg: "#b45309", text: "#fff" },
};

// ─── Plan Card (pure display, no carousel logic inside) ───────────────────────
const PlanCard = React.memo(({ plan, yearKey, isActive, onBuy }) => {
  const data = ALL_PLAN_DETAILS[plan.key][yearKey];
  const badgeStyle = BADGE_COLORS[plan.badge] || {};

  return (
    <div
      className="flex flex-col h-full rounded-3xl overflow-hidden bg-white"
      style={{
        border: isActive ? "2px solid rgba(87,46,148,0.35)" : "2px solid rgba(0,0,0,0.06)",
        boxShadow: isActive
          ? "0 24px 60px rgba(87,46,148,0.18), 0 4px 16px rgba(87,46,148,0.08)"
          : "0 4px 20px rgba(0,0,0,0.06)",
      }}
    >
      {/* ── Header ── */}
      <div
        className="px-6 pt-6 pb-5 flex-shrink-0"
        style={{
          background: isActive
            ? "linear-gradient(135deg, #f3eeff 0%, #faf7ff 100%)"
            : "linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)",
        }}
      >
        {/* Badge row */}
        <div className="flex items-center justify-between mb-3 min-h-[24px]">
          {plan.badge ? (
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
              style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white/70 inline-block" />
              {plan.badge}
            </span>
          ) : <span />}
          {isActive && (
            <motion.span
              layoutId="active-dot"
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: "#572e94", boxShadow: "0 0 0 4px rgba(87,46,148,0.18)" }}
            />
          )}
        </div>

        {/* Name */}
        <h3 className={`font-bold leading-tight ${isActive ? "text-xl text-gray-900" : "text-base text-gray-700"}`}>
          {plan.name}
        </h3>
        <p className="text-xs text-gray-400 mt-1 leading-snug">{plan.tagline}</p>

        {/* Price */}
        <div className="mt-4 flex items-end gap-1.5">
          <AnimatePresence mode="wait">
            <motion.span
              key={data.price}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`font-extrabold tracking-tight ${isActive ? "text-4xl text-gray-900" : "text-3xl text-gray-700"}`}
            >
              {data.price}
            </motion.span>
          </AnimatePresence>
          <span className="text-sm text-gray-400 mb-1">+ GST</span>
        </div>

        {/* Validity chip */}
        <div className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full" style={{ background: isActive ? "rgba(87,46,148,0.08)" : "rgba(0,0,0,0.04)" }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5" stroke={isActive ? "#572e94" : "#9ca3af"} strokeWidth="1.2" />
            <path d="M6 3.5v3l1.5 1.5" stroke={isActive ? "#572e94" : "#9ca3af"} strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <span className={`text-xs font-semibold ${isActive ? "text-[#572e94]" : "text-gray-400"}`}>
            Valid for {data.validity}
          </span>
        </div>

        {/* CTA */}
        <button
          onClick={(e) => { e.stopPropagation(); onBuy(data.planCode, plan.members); }}
          disabled={!isActive}
          className="mt-4 w-full rounded-2xl py-3 text-sm font-semibold text-white transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
          style={{
            background: isActive
              ? "linear-gradient(135deg, #7c3aed 0%, #572e94 100%)"
              : "linear-gradient(135deg, #1AAD9D 0%, #0E8F82 100%)",
            boxShadow: isActive ? "0 4px 16px rgba(87,46,148,0.35)" : "0 2px 8px rgba(26,173,157,0.25)",
          }}
        >
          Buy Plan
        </button>
      </div>

      {/* ── Highlights strip ── */}
      <div className="px-5 py-4 flex-shrink-0" style={{ background: isActive ? "#faf7ff" : "#f9fafb", borderTop: "1px solid", borderColor: isActive ? "rgba(87,46,148,0.1)" : "rgba(0,0,0,0.05)" }}>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: isActive ? "#7c3aed" : "#9ca3af" }}>
          What's included
        </p>
        <ul className="space-y-1.5">
          {plan.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-snug">
              <CheckIcon active={isActive} />
              {h}
            </li>
          ))}
        </ul>
      </div>

      {/* ── Full details ── */}
      <div className="px-5 py-4 flex-1 overflow-y-auto" style={{ maxHeight: 420 }}>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Full Plan Details</p>
        {plan.sections.map((section, si) => (
          <div key={si}>
            {section.heading && <SectionLabel heading={section.heading} sub={section.headingSub} />}
            <ul className="space-y-2.5 mb-1">
              {section.items.map((item, ii) => {
                if (typeof item === "string") {
                  return (
                    <li key={ii} className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
                      <CheckIcon active={isActive} />
                      <span>{item}</span>
                    </li>
                  );
                }
                return (
                  <li key={ii} className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
                    <CheckIcon active={isActive} />
                    <span>
                      {item.text}&nbsp;
                      <Tooltip content={item.tooltip} position="top">
                        <span className="border-b border-dashed border-gray-400 cursor-pointer">{item.label}</span>
                      </Tooltip>
                      {item.suffix && <><br /><span className="text-xs text-gray-400">{item.suffix}</span></>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
});
PlanCard.displayName = "PlanCard";

// ─── Dot Indicator ───────────────────────────────────────────────────────────
const DotIndicator = ({ count, active, onChange }) => (
  <div className="flex items-center gap-2 justify-center mt-6">
    {Array.from({ length: count }).map((_, i) => (
      <button key={i} onClick={() => onChange(i)} aria-label={`Go to plan ${i + 1}`} className="focus:outline-none p-1">
        <motion.div
          animate={{ width: i === active ? 28 : 8, backgroundColor: i === active ? "#572e94" : "#D1D5DB" }}
          transition={{ duration: 0.25 }}
          style={{ height: 8, borderRadius: 4 }}
        />
      </button>
    ))}
  </div>
);

// ─── Carousel offset calculator ───────────────────────────────────────────────
// FIX: Now receives CARD_W and ACTIVE_W as arguments instead of using stale constants.
function calcOffset(index, viewportW, CARD_W, ACTIVE_W) {
  const leftEdge = index * (CARD_W + GAP);
  const center = leftEdge + ACTIVE_W / 2;
  return viewportW / 2 - center;
}

// ─── Main Page ───────────────────────────────────────────────────────────────
const PlanPage = () => {
  const [openPopup, setOpenPopup] = useState(false);
  const [planCode, setPlanCode] = useState("");
  const [tenantId] = useState("T4236001XTE");
  const [isRider] = useState(false);
  const [showMember] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [showRC] = useState(false);

  const [isTwoYear, setIsTwoYear] = useState(false);
  const yearKey = isTwoYear ? "2yr" : "1yr";

  const visiblePlans = useMemo(
    () => PLANS_META.filter((plan) => !!ALL_PLAN_DETAILS[plan.key]?.[yearKey]),
    [yearKey]
  );
  const TOTAL = visiblePlans.length;
  const [selectedKey, setSelectedKey] = useState(PLANS_META[2].key);
  const activeIndex = Math.max(0, visiblePlans.findIndex((p) => p.key === selectedKey));

  useEffect(() => {
    if (visiblePlans.some((p) => p.key === selectedKey)) return;
    const originalIdx = PLANS_META.findIndex((p) => p.key === selectedKey);
    let fallbackKey = visiblePlans[0]?.key;
    for (let i = originalIdx - 1; i >= 0; i--) {
      if (visiblePlans.some((p) => p.key === PLANS_META[i].key)) {
        fallbackKey = PLANS_META[i].key;
        break;
      }
    }
    if (fallbackKey) setSelectedKey(fallbackKey);
  }, [visiblePlans, selectedKey]);

  const [viewportW, setViewportW] = useState(0);

  // FIX: Derive card sizes from live viewport width on every render
  const { CARD_W, ACTIVE_W } = getCardSizes(viewportW);

  const trackX = useMotionValue(0);
  const viewportRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartTrackX = useRef(0);
  const isDragging = useRef(false);

  // Measure viewport
  useLayoutEffect(() => {
    const measure = () => {
      if (viewportRef.current) setViewportW(viewportRef.current.offsetWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (viewportRef.current) ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, []);

  // Snap to active index whenever activeIndex or viewportW changes
  // FIX: Pass CARD_W and ACTIVE_W so calcOffset uses the correct responsive values
  useEffect(() => {
    if (!viewportW) return;
    const target = calcOffset(activeIndex, viewportW, CARD_W, ACTIVE_W);
    animate(trackX, target, { type: "spring", stiffness: 380, damping: 36, mass: 0.8 });
  }, [activeIndex, viewportW, CARD_W, ACTIVE_W]);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(visiblePlans.length - 1, idx));
    const plan = visiblePlans[clamped];
    if (plan) setSelectedKey(plan.key);
  }, [visiblePlans]);

  const openBuy = (code, members = 0) => {
    setPlanCode(code);
    setMemberCount(members);
    setOpenPopup(true);
  };

  // Total track width (all cards, each treated as CARD_W except active which is ACTIVE_W — but for drag bounds we use a simpler calc)
  const trackW = TOTAL * CARD_W + (TOTAL - 1) * GAP + (ACTIVE_W - CARD_W); // extra for active card
  const minX = viewportW / 2 - trackW + CARD_W / 2;
  const maxX = viewportW / 2 - ACTIVE_W / 2;

  const onDragStart = (e) => {
    if (e.target.closest("button")) { isDragging.current = false; return; }
    isDragging.current = true;
    dragStartX.current = e.clientX ?? 0;
    dragStartTrackX.current = trackX.get();
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onDragMove = (e) => {
    if (!isDragging.current) return;
    if (e.buttons === 0 && e.type === "pointermove") return;
    const currX = e.clientX ?? 0;
    const delta = currX - dragStartX.current;
    trackX.set(Math.max(minX, Math.min(maxX, dragStartTrackX.current + delta)));
  };

  const onDragEnd = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const currX = e.clientX ?? dragStartX.current;
    const delta = currX - dragStartX.current;
    if (Math.abs(delta) > 40) {
      goTo(activeIndex + (delta < 0 ? 1 : -1));
    } else {
      animate(trackX, calcOffset(activeIndex, viewportW, CARD_W, ACTIVE_W), { type: "spring", stiffness: 380, damping: 36 });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <section className="py-16 lg:py-24 max-md:pt-[110px]">
        <div className="px-2 sm:px-6 lg:px-8">

          {/* ── Header ── */}
          <div className="text-center mx-auto max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 mb-5" style={{ borderColor: "rgba(87,46,148,0.25)", background: "#faf7ff" }}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: "#572e94" }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: "#572e94" }} />
              </span>
              <span className="text-sm font-medium" style={{ color: "#572e94" }}>Simple Pricing</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight">
              Pick Your Perfect<br className="sm:hidden" /> Health Plan
            </h2>
            <p className="mt-3 text-gray-500 text-sm leading-relaxed max-w-lg mx-auto">
              Choose a plan that fits your lifestyle, with the flexibility to upgrade anytime and access healthcare support from day one.
            </p>
          </div>

          {/* ── Global Validity Toggle ── */}
          <div className="flex flex-col items-center mb-10 gap-3">
            <div className="inline-flex items-center gap-4" >
               {/* rounded-2xl px-6 py-3.5 shadow-sm */}
              {/* style={{ background: "#fff", border: "1.5px solid rgba(87,46,148,0.12)" }} */}
              <span className={`text-sm font-semibold transition-colors select-none ${!isTwoYear ? "text-gray-900" : "text-gray-400"}`}>1 Year</span>
              <Switch checked={isTwoYear} onCheckedChange={setIsTwoYear} size="lg" defaultChecked={true} />
              <div className="flex flex-col items-start gap-0.5">
                <span className={`text-sm font-semibold transition-colors select-none ${isTwoYear ? "text-gray-900" : "text-gray-400"}`}>2 Years</span>
                {/* <AnimatePresence>
                  {isTwoYear && (
                    <motion.span
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="text-xs font-bold overflow-hidden"
                      style={{ color: "#572e94" }}
                    >
                      Save ~10%
                    </motion.span>
                  )}
                </AnimatePresence> */}
              </div>
            </div>
            {/* <p className="text-xs text-gray-400">{isTwoYear ? "2-year validity — best value" : "1-year validity — pay as you go"}</p> */}
          </div>

          {/* ── Plan name tabs (quick jump) ── */}
          <div className="flex items-center justify-center gap-2 flex-wrap mb-6 px-4">
            {visiblePlans.map((plan, i) => (
              <button
                key={plan.key}
                onClick={() => goTo(i)}
                className="text-xs px-3 py-1.5 rounded-full font-medium transition-all focus:outline-none"
                style={{
                  background: i === activeIndex ? "#572e94" : "rgba(87,46,148,0.07)",
                  color: i === activeIndex ? "#fff" : "#6b7280",
                  boxShadow: i === activeIndex ? "0 2px 10px rgba(87,46,148,0.28)" : "none",
                }}
              >
                {plan.name.replace("Saayaa ", "")}
              </button>
            ))}
          </div>

          {/* ── Carousel ── */}
          <div className="relative">
            {/* Left arrow */}
            <motion.button
              onClick={() => goTo(activeIndex - 1)}
              disabled={activeIndex === 0}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 disabled:opacity-25 disabled:pointer-events-none"
              style={{ boxShadow: "0 4px 20px rgba(87,46,148,0.18)", border: "1.5px solid rgba(87,46,148,0.15)" }}
              aria-label="Previous plan"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 14l-5-5 5-5" stroke="#572e94" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.button>

            {/* Right arrow */}
            <motion.button
              onClick={() => goTo(activeIndex + 1)}
              disabled={activeIndex === TOTAL - 1}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 disabled:opacity-25 disabled:pointer-events-none"
              style={{ boxShadow: "0 4px 20px rgba(87,46,148,0.18)", border: "1.5px solid rgba(87,46,148,0.15)" }}
              aria-label="Next plan"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 4l5 5-5 5" stroke="#572e94" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.button>

            {/* Viewport: clips the track */}
            <div
              ref={viewportRef}
              className="overflow-hidden w-full"
              style={{ cursor: "grab", userSelect: "none",touchAction: "pan-y" }}
              // onPointerDown={onDragStart}
              // onPointerMove={onDragMove}
              // onPointerUp={onDragEnd}
              // onPointerCancel={onDragEnd}
              // onTouchStart={(e) => { dragStartX.current = e.touches[0].clientX; dragStartTrackX.current = trackX.get();}}
              // onTouchMove={(e) => { const d = e.touches[0].clientX - dragStartX.current; trackX.set(Math.max(minX, Math.min(maxX, dragStartTrackX.current + d)));}}
              // onTouchEnd={(e) => {const d = e.changedTouches[0].clientX - dragStartX.current;if (Math.abs(d) > 40) goTo(activeIndex + (d < 0 ? 1 : -1));
              //   else animate(trackX, calcOffset(activeIndex, viewportW, CARD_W, ACTIVE_W), { type: "spring", stiffness: 380, damping: 36 });}}
            >
              {/* Track */}
              <motion.div
                style={{ x: trackX, display: "flex", alignItems: "flex-start", gap: GAP, paddingBottom: 24, paddingTop: 8 }}
              >
                {visiblePlans.map((plan, i) => {
                  const isActive = i === activeIndex;
                  const distance = Math.abs(i - activeIndex);
                  const scale = isActive ? 1 : Math.max(0.88, 1 - distance * 0.06);
                  const opacity = isActive ? 1 : Math.max(0.5, 1 - distance * 0.22);

                  return (
                    <motion.div
                      key={plan.key}
                      animate={{ scale, opacity, width: isActive ? ACTIVE_W : CARD_W }}
                      transition={{ type: "spring", stiffness: 340, damping: 32 }}
                      style={{ flexShrink: 0, originY: 0 }}
                      onClick={() => !isActive && goTo(i)}
                    >
                      <PlanCard
                        plan={plan}
                        yearKey={yearKey}
                        isActive={isActive}
                        onBuy={openBuy}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </div>

          {/* ── Dot nav ── */}
          <DotIndicator count={TOTAL} active={activeIndex} onChange={goTo} />

          {/* ── Counter ── */}
          <p className="text-center text-xs text-gray-400 mt-2">
            {activeIndex + 1} of {TOTAL} plans &nbsp;·&nbsp;
            <span className="md:hidden">Swipe to explore</span>
            <span className="hidden md:inline">Use arrows or click a plan to navigate</span>
          </p>
        </div>
      </section>

      <BuyPlan
        planCode={planCode}
        tenantId={tenantId}
        showPopUp={openPopup}
        setShowPopUp={setOpenPopup}
        isRider={isRider}
        showMember={showMember}
        memberCount={memberCount}
        showRC={showRC}
      />
    </div>
  );
};

export default PlanPage;